import { z } from 'zod';
/** Generic GIS assets (AFM requirement f): poles, cabinets, transformer stations, sockets, parking, networks, … */
export const GeoJsonGeometry = z.object({
    type: z.enum(['Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon']),
    coordinates: z.unknown(),
});
export const AssetPropertyDef = z.object({
    key: z.string(),
    type: z.enum(['string', 'number', 'integer', 'boolean', 'date', 'enum']),
    unit: z.string().optional(),
    options: z.array(z.string()).optional(),
    required: z.boolean().optional(),
});
export const AssetType = z.object({
    key: z.string(),
    name_en: z.string(),
    geometry_kind: z.enum(['point', 'line', 'polygon', 'any']),
    color: z.string().nullable(),
    icon: z.string().nullable(),
    properties_schema: z.array(AssetPropertyDef),
    is_system: z.boolean(),
});
export const AssetTypeList = z.object({ items: z.array(AssetType) });
export const AssetStatus = z.enum(['planned', 'active', 'maintenance', 'faulty', 'retired']);
export const Asset = z.object({
    id: z.uuid(),
    client_id: z.uuid(),
    client_name: z.string(),
    site_id: z.uuid().nullable(),
    site_name: z.string().nullable(),
    type_key: z.string(),
    name: z.string(),
    code: z.string().nullable(),
    status: AssetStatus,
    geometry: GeoJsonGeometry.nullable(),
    properties: z.record(z.string(), z.unknown()),
    installed_on: z.string().nullable(),
    notes: z.string().nullable(),
    updated_at: z.string(),
    can_manage: z.boolean(),
});
export const AssetList = z.object({ items: z.array(Asset), total: z.number().int() });
export const AssetSave = z.object({
    client_id: z.uuid(),
    site_id: z.uuid().nullable().optional(),
    type_key: z.string().min(1).max(41),
    name: z.string().trim().min(1).max(200),
    code: z.string().trim().max(100).nullable().optional(),
    status: AssetStatus.optional(),
    geometry: GeoJsonGeometry.nullable().optional(),
    properties: z
        .record(z.string(), z.union([z.string().max(500), z.number(), z.boolean(), z.null()]))
        .optional(),
    installed_on: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    notes: z.string().max(4000).nullable().optional(),
});
export const RelationKind = z.enum([
    'mounted_on',
    'fed_from',
    'part_of',
    'controls',
    'connected_to',
    'located_in',
]);
export const SubjectType = z.enum(['asset', 'device']);
export const Relation = z.object({
    id: z.uuid(),
    from_type: SubjectType,
    from_id: z.uuid(),
    from_name: z.string(),
    kind: RelationKind,
    to_type: SubjectType,
    to_id: z.uuid(),
    to_name: z.string(),
});
export const RelationCreate = z.object({
    from_type: SubjectType,
    from_id: z.uuid(),
    kind: RelationKind,
    to_type: SubjectType,
    to_id: z.uuid(),
});
export const MaintenanceKind = z.enum(['maintenance', 'inspection', 'repair', 'replacement', 'note']);
export const SubjectEvent = z.object({
    id: z.uuid(),
    kind: z.string(),
    at: z.string(),
    author: z.string().nullable(),
    title: z.string().nullable(),
    body: z.string().nullable(),
    data: z.record(z.string(), z.unknown()),
});
export const SubjectEventCreate = z.object({
    subject_type: SubjectType,
    subject_id: z.uuid(),
    kind: MaintenanceKind,
    at: z.iso.datetime({ offset: true }).optional(),
    title: z.string().trim().min(1).max(200),
    body: z.string().max(8000).optional(),
});
export const SubjectDocument = z.object({
    id: z.uuid(),
    filename: z.string(),
    content_type: z.string(),
    size_bytes: z.number().int(),
    note: z.string().nullable(),
    uploaded_by: z.string().nullable(),
    uploaded_at: z.string(),
});
/** GET /api/v1/assets/:id and GET /api/v1/subjects/device/:id — relations, history and documents of a subject. */
export const SubjectDetail = z.object({
    asset: Asset.nullable(),
    relations: z.array(Relation),
    events: z.array(SubjectEvent),
    documents: z.array(SubjectDocument),
    can_manage: z.boolean(),
});
export const AssetImportResult = z.object({
    created: z.number().int(),
    skipped: z.number().int(),
    errors: z.array(z.string()),
});
//# sourceMappingURL=gis.js.map